import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from "react-router-dom";
import { Paper, Button, ButtonGroup, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import {
    PlayArrow as PlayArrowIcon, Pause as PauseIcon, Delete as DeleteIcon, Dangerous as DangerousIcon, Replay as ReplayIcon
} from '@mui/icons-material';
import { frFR, enUS } from '@mui/x-data-grid/locales';
import { formatDateTime } from '@utils/helpers';
import API from '@/utils/axios';
import theme from '@/theme';
import { useAuth } from '@contexts/AuthContext';
import { useSnackbar } from '@contexts/SnackbarContext';
import { useDialog } from '@contexts/DialogContext';
import useLocalStorage from '@/hooks/useLocalStorage';
import OurPagination from '@components/OurPagination';
import OurDataToolbar from '@components/OurDataToolbar';
import SendoutSearch from '@components/sendout/SendoutSearch';
import SendoutStatusChip from '@components/sendout/SendoutStatusChip';
import SendoutIsTestChip from '@components/sendout/SendoutIsTestChip';
import SendoutName from '@components/sendout/SendoutName';

function SendoutTable() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { showMessage } = useSnackbar();
    const { requestConfirm } = useDialog();
    const { doLogout, isSuperAdmin } = useAuth();
    const [loading, setLoading] = useState(true);
    const [sendouts, setSendouts] = useState([]);
    const [rowCount, setRowCount] = useState(0);
    const [paginationModel, setPaginationModel] = useLocalStorage('sendouts.pagination', {
        pageSize: 10,
        page: 0,
    });
    const [columnVisibility, setColumnVisibility] = useLocalStorage('sendouts.columns', {
        description: false,
        customer_id: false,
        created_at: false,
        created_by: false,
        updated_at: false,
    });
    const [density, setDensity] = useLocalStorage('sendouts.density', 'standard');
    const [searchVisible, setSearchVisible] = useLocalStorage('sendouts.searchVisible', false);
    const [filter, setFilter] = useLocalStorage('sendouts.searchValues', {});

    const openEditPage = (params) => {
        // Ne pas ouvrir si clic sur un bouton ou lien de la liste
        if (params.field) return;
        navigate(`/sendouts/${params.id}`);
    };

    const toggleSearch = () => {
        if (searchVisible) {
            setFilter({});
        }
        setSearchVisible(!searchVisible);
    };

    const loadSendouts = useCallback(async (paginationModel) => {
        try {
            setLoading(true);
            let params = {
                per_page: paginationModel.pageSize,
                page: paginationModel.page + 1,
            };
            const response = await API.get('/sendouts', { params: { ...params, ...filter } });
            setSendouts(
                response.data.data.map((sendout) => ({
                    id: sendout.id,
                    description: sendout.description,
                    status: sendout.status,
                    missing_credits_type: sendout.missing_credits_type,
                    planned_at: sendout.planned_at,
                    error_count: sendout.error_count,
                    is_test: sendout.is_test,
                    workflow_id: sendout.workflow_id,
                    workflow_name: sendout.workflow_name,
                    workflow_customer_id: sendout.workflow_customer_id,
                    mailing_list_id: sendout.mailing_list_id,
                    mailing_list_name: sendout.mailing_list_name,
                    customer_id: sendout.customer_id,
                    customer_name: sendout.customer_name,
                    created_at: new Date(sendout.created_at),
                    created_by: sendout.created_by ?? null,
                    updated_at: new Date(sendout.updated_at),
                }))
            );
            setRowCount(response.data.total);
            setLoading(false);
        } catch (error) {
            setLoading(false);
            if (error.response && error.response.status === 401) {
                doLogout();
            } else {
                console.error('Erreur de chargement', error);
            }
        }
    }, [doLogout, filter]);

    useEffect(() => {
        loadSendouts(paginationModel);
    }, [loadSendouts, paginationModel]);

    const changeStatus = async (id, status) => {
        try {
            await API.put(`/sendouts/${id}`, { status });
            loadSendouts(paginationModel);
            switch (status) {
                case 'InProgress':
                    showMessage(t('Sendout resumed'), 'success');
                    break;
                case 'Paused':
                    showMessage(t('Sendout paused'), 'success');
                    break;
                case 'Canceled':
                    showMessage(t('Sendout canceled'), 'success');
                    break;
                default:
                    break;
            }
        } catch (error) {
            if (error.response && error.response.status === 401) {
                doLogout();
            } else if (error.response && error.response.status === 403) {
                // Erreur 403 : probablement car le statut a changé entre temps
                showMessage(t('Unable to change status'), 'error');
                loadSendouts(paginationModel);
            } else {
                console.error('Erreur de changement de statut', error);
                showMessage(t('Internal error.'), 'error');
            }
        }
    };

    const confirmAndCancel = (id, status) => {
        requestConfirm({
            message: t('Are you sure you want to cancel this sendout?')
                + ' ' + t('The cancellation is final; this sending cannot be resumed.'),
            onConfirm: () => { changeStatus(id, status) },
        });
    };

    const confirmAndResumeSms = (id, status) => {
        requestConfirm({
            message: t('Are you sure you want to resume this sendout?')
                + ' ' + t('Please ensure you have enough {{type}} credits before resuming the sendout.', { type: t('credit.sms') }),
            onConfirm: () => { changeStatus(id, status) },
        });
    };

    const deleteSendout = async (id) => {
        try {
            await API.delete(`/sendouts/${id}`);
            loadSendouts(paginationModel);
            showMessage(t('Sendout deleted'), 'success');
        } catch (error) {
            if (error.response && error.response.status === 401) {
                doLogout();
            } else {
                console.error('Erreur de suppression', error);
                showMessage(t('Internal error.'), 'error');
            }
        }
    };

    const confirmAndDeleteSendout = (id) => {
        requestConfirm({
            message: t('Are you sure you want to delete this sendout?')
                + ' ' + t('All history and related data will be lost.'),
            onConfirm: () => { deleteSendout(id) },
        });
    };

    const confirmAndRetrySendout = (sendout) => {
        requestConfirm({
            message: t('Are you sure you want to restart this sendout?')
                + ' ' + t('A new sendout will be created, and the selected sendout will be deleted.')
                + ' ' + t('All history will be lost, and all processing, even if already executed, will be launched again.')
                + ' ' + t('This option should only be used in certain special cases.'),
            onConfirm: () => { retrySendout(sendout) },
        });
    };

    const retrySendout = async (sendout) => {
        try {
            await API.delete(`/sendouts/${sendout.id}`);
            await API.post('/sendouts', {
                workflow_id: sendout.workflow_id,
                mailing_list_id: sendout.mailing_list_id,
                description: sendout.description,
                is_test: sendout.is_test,
                status: 'InProgress',
            });
            loadSendouts(paginationModel);
            showMessage(t('Sendout restarted'), 'success');
        } catch (error) {
            if (error.response && error.response.status === 401) {
                doLogout();
            } else {
                console.error('Erreur de redémarrage', error);
                showMessage(t('Internal error.'), 'error');
            }
        }
    };

    const localeTheme = i18n.language === 'fr' ? frFR : enUS;
    const themeWithLocale = createTheme(theme, localeTheme);

    const columns = [
        {
            field: 'id',
            headerName: t('ID'),
            flex: 0.5,
            type: 'number',
            maxWidth: 70,
        },
        {
            field: 'details',
            headerName: t('Details'),
            flex: 2,
            type: 'string',
            renderCell: (params) => <SendoutName row={params.row} links={true} />,
        },
        {
            field: 'description',
            headerName: t('Description'),
            flex: 2,
            type: 'string',
        },
        {
            field: 'status',
            headerName: t('Status'),
            flex: 1,
            type: 'string',
            renderCell: (params) => {
                return <SendoutStatusChip
                    status={params.value}
                    missing_credits_type={params.row.missing_credits_type}
                    planned_at={params.row.planned_at}
                />;
            }
        },
        {
            field: 'error_count',
            headerName: t('Errors'),
            flex: 0.5,
            type: 'string',
            renderCell: (params) => params.value > 0
                ? <Typography
                    color="error"
                    variant="body2"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        height: '100%'
                    }}
                >
                    {params.value > 1
                        ? t('{{count}} errors', { count: params.value })
                        : t('1 error')
                    }
                </Typography>
                : null,
        },
        {
            field: 'is_test',
            headerName: t('Type'),
            flex: 0.5,
            type: 'string',
            renderCell: (params) => <SendoutIsTestChip is_test={params.value} />,
        },
        ...(isSuperAdmin() ? [
            {
                field: 'customer_id',
                headerName: t('Customer ID'),
                flex: 1,
                type: 'number'
            },
            {
                field: 'customer_name',
                headerName: t('Customer'),
                flex: 1,
                type: 'string'
            },
        ] : []),
        {
            field: 'created_at',
            headerName: t('Creation date'),
            flex: 1,
            type: 'dateTime',
            valueFormatter: formatDateTime
        },
        {
            field: 'created_by',
            headerName: t('Created by'),
            flex: 1,
            type: 'string',
            renderCell: (params) => params.value
                ? <>{params.value.first_name} {params.value.last_name}</>
                : null
        },
        {
            field: 'updated_at',
            headerName: t('Update date'),
            flex: 1,
            type: 'dateTime',
            valueFormatter: formatDateTime
        },
        {
            field: 'actions',
            headerName: t('Actions'),
            flex: 1,
            sortable: false,
            disableColumnMenu: true,
            renderCell: function (params) {
                switch (params.row.status) {
                    case 'InProgress':
                        return <ButtonGroup size="small" variant="contained" sx={{ mt: 1 }}>
                            <Button startIcon={<PauseIcon />}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    changeStatus(params.row.id, 'Paused');
                                }}>{t('Pause')}</Button>
                            <Button startIcon={<DangerousIcon />}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    confirmAndCancel(params.row.id, 'Canceled');
                                }}>{t('Cancel')}</Button>
                        </ButtonGroup>;

                    case 'Paused':
                        return <ButtonGroup size="small" variant="contained" sx={{ mt: 1 }}>
                            <Button startIcon={<PlayArrowIcon />}
                                onClick={(event) => {
                                    changeStatus(params.row.id, 'InProgress');
                                    event.stopPropagation();
                                }}>{t('Resume')}</Button>
                            <Button startIcon={<DangerousIcon />}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    confirmAndCancel(params.row.id, 'Canceled');
                                }}>{t('Cancel')}</Button>
                        </ButtonGroup>;

                    case 'PausedMissingCredits':
                        return <ButtonGroup size="small" variant="contained" sx={{ mt: 1 }}>
                            <Button startIcon={<PlayArrowIcon />}
                                onClick={(event) => {
                                    confirmAndResumeSms(params.row.id, 'InProgress');
                                    event.stopPropagation();
                                }}>{t('Resume')}</Button>
                            <Button startIcon={<DangerousIcon />}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    confirmAndCancel(params.row.id, 'Canceled');
                                }}>{t('Cancel')}</Button>
                        </ButtonGroup>;

                    case 'Planned':
                        return <ButtonGroup size="small" variant="contained" sx={{ mt: 1 }}>
                            <Button startIcon={<DangerousIcon />}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    confirmAndCancel(params.row.id, 'Canceled');
                                }}>{t('Cancel')}</Button>
                        </ButtonGroup>;

                    case 'Completed':
                        return <ButtonGroup size="small" variant="contained" sx={{ mt: 1 }}>
                            <Button color="error" startIcon={<DeleteIcon />}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    confirmAndDeleteSendout(params.row.id);
                                }}>{t('Delete')}</Button>
                            {isSuperAdmin() &&
                                <Button startIcon={<ReplayIcon />}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        confirmAndRetrySendout(params.row);
                                    }}>{t('Retry')}</Button>
                            }
                        </ButtonGroup>;

                    case 'Canceled':
                        return <ButtonGroup size="small" variant="contained" sx={{ mt: 1 }}>
                            <Button color="error" startIcon={<DeleteIcon />}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    confirmAndDeleteSendout(params.row.id);
                                }}>{t('Delete')}</Button>
                            {isSuperAdmin() &&
                                <Button startIcon={<ReplayIcon />}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        confirmAndRetrySendout(params.row);
                                    }}>{t('Retry')}</Button>
                            }
                        </ButtonGroup>;
                    default:
                        return null;
                }
            }
        }
    ];

    return (
        <ThemeProvider theme={themeWithLocale}>
            <Paper>
                {searchVisible && <SendoutSearch handleSubmit={setFilter} filter={filter} />}
                <DataGrid
                    autoHeight
                    loading={loading}
                    slots={{
                        toolbar: OurDataToolbar,
                        pagination: OurPagination,
                    }}
                    slotProps={{
                        toolbar: { searchVisible, toggleSearch, refreshCallback: () => loadSendouts(paginationModel) },
                    }}
                    rows={sendouts}
                    columns={columns}
                    pageSizeOptions={[10, 15, 20, 50, 100]}
                    pagination
                    paginationMode="server"
                    rowCount={rowCount}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    columnVisibilityModel={columnVisibility}
                    onColumnVisibilityModelChange={(newModel) =>
                        setColumnVisibility(newModel)
                    }
                    disableColumnSorting
                    density={density}
                    onDensityChange={setDensity}
                    disableRowSelectionOnClick
                    disableSelectionOnClick
                    disableColumnResize={true}
                    disableColumnFilter
                    onRowClick={openEditPage}
                />

            </Paper>

        </ThemeProvider>
    );
}

export default SendoutTable;
